import { useQuery } from "@tanstack/react-query";
import type React from "react";
import { useDeferredValue, useEffect, useId, useRef, useState } from "react";
import { twJoin } from "tailwind-merge";

import type { MentionDto, MentionInputDto, MentionSuggestionDto, MessageTypeDto } from "@/api/types";
import { Building02Icon } from "@/components/Icons/Building02Icon";
import { UserAvatar } from "@/components/UserAvatar/UserAvatar";
import { TextArea } from "@/design-system/TextArea";
import { mergeRefs } from "@/helpers/mergeRefs";
import { useCommunityFeedQueries } from "@/queries/community-feed/queryOptions";
import { useTranslation } from "@/translations";

/** Structured mention fields are either absent together or present as one synchronized value. */
export type StructuredMentionFields =
  | { contentTemplate?: undefined; mentions?: undefined }
  | { contentTemplate: string; mentions: MentionInputDto[] };

export type MentionComposerValue = StructuredMentionFields & {
  content: string;
};

/** Returns the synchronized mention fields without exposing the composer's plain content. */
export function getStructuredMentionFields(value: MentionComposerValue): StructuredMentionFields {
  return value.contentTemplate === undefined
    ? {}
    : { contentTemplate: value.contentTemplate, mentions: value.mentions };
}

const protectedMentionPattern = /[ \t]*\[\[AOP_MENTION:([^\]]+)]]/g;

/** Keeps mention identity next to its readable display text while an external text rewriter processes the content. */
export function createMentionProtectedText(value: {
  content: string;
  contentTemplate?: string;
  mentions?: MentionInputDto[];
}): string {
  if (!value.contentTemplate || !value.mentions?.length) {
    return value.content;
  }
  const mentionsById = new Map(value.mentions.map((mention) => [mention.id, mention]));
  let isComplete = true;
  const protectedText = value.contentTemplate.replace(/\{\{mention:([^}]+)}}/g, (_token, id: string) => {
    const mention = mentionsById.get(id);
    if (!mention) {
      isComplete = false;

      return "";
    }

    return `@${mention.text.replace(/^@/, "")} [[AOP_MENTION:${mention.id}]]`;
  });

  return isComplete ? protectedText : value.content;
}

/** Restores protected mention markers after a rewrite, falling back to exact readable names when markers were removed. */
export function restoreMentionsAfterRewrite(
  rewrittenContent: string,
  original: { content: string; contentTemplate?: string; mentions?: MentionInputDto[] },
): MentionComposerValue | undefined {
  if (!original.contentTemplate || !original.mentions?.length) {
    return { content: rewrittenContent };
  }
  const mentionsById = new Map(original.mentions.map((mention) => [mention.id, mention]));
  const orderedIds = [...original.contentTemplate.matchAll(/\{\{mention:([^}]+)}}/g)].map((match) => match[1]);
  if (orderedIds.length !== original.mentions.length || orderedIds.some((id) => !mentionsById.has(id))) {
    return undefined;
  }

  const markerAnchors: Array<{ id: string; end: number }> = [];
  let content = "";
  let cursor = 0;
  for (const match of rewrittenContent.matchAll(protectedMentionPattern)) {
    if (match.index == null) {
      continue;
    }
    content += rewrittenContent.slice(cursor, match.index);
    const mention = mentionsById.get(match[1]);
    const display = mention ? `@${mention.text.replace(/^@/, "")}` : undefined;
    const isProtectedMarker =
      display !== undefined && content.endsWith(display) && !markerAnchors.some(({ id }) => id === match[1]);
    if (isProtectedMarker) {
      markerAnchors.push({ id: match[1], end: content.length });
    } else {
      content += match[0];
    }
    cursor = match.index + match[0].length;
  }
  content += rewrittenContent.slice(cursor);

  const exactMarkerSet =
    markerAnchors.length === orderedIds.length &&
    new Set(markerAnchors.map(({ id }) => id)).size === orderedIds.length &&
    orderedIds.every((id) => markerAnchors.some((marker) => marker.id === id));
  const selected: SelectedMention[] = [];
  if (exactMarkerSet) {
    for (const marker of markerAnchors) {
      const mention = mentionsById.get(marker.id);
      if (!mention) {
        return undefined;
      }
      const display = `@${mention.text.replace(/^@/, "")}`;
      const start = content.lastIndexOf(display, marker.end - display.length);
      if (start < 0 || start + display.length !== marker.end || selected.some((item) => item.start === start)) {
        return undefined;
      }
      selected.push({ ...mention, text: mention.text.replace(/^@/, ""), start, end: start + display.length });
    }
  } else {
    for (const id of orderedIds) {
      const mention = mentionsById.get(id);
      if (!mention) {
        return undefined;
      }
      const display = `@${mention.text.replace(/^@/, "")}`;
      const starts: number[] = [];
      let searchFrom = 0;
      while (searchFrom < content.length) {
        const start = content.indexOf(display, searchFrom);
        if (start < 0) {
          break;
        }
        const previous = content[start - 1];
        const next = content[start + display.length];
        if ((!previous || !/[\p{L}\p{N}_]/u.test(previous)) && (!next || !/[\p{L}\p{N}_]/u.test(next))) {
          starts.push(start);
        }
        searchFrom = start + display.length;
      }
      if (starts.length !== 1) {
        return undefined;
      }
      const [start] = starts;
      const end = start + display.length;
      if (selected.some((item) => start < item.end && end > item.start)) {
        return undefined;
      }
      selected.push({ ...mention, text: mention.text.replace(/^@/, ""), start, end });
    }
  }

  return buildStructuredValue(
    content,
    selected.sort((a, b) => a.start - b.start),
  );
}

interface SelectedMention extends MentionInputDto {
  start: number;
  end: number;
}

interface MentionComposerProps extends Omit<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  "onChange" | "onSubmit" | "size" | "value"
> {
  value: MentionComposerValue;
  onChange: (value: MentionComposerValue) => void;
  "data-testid"?: string;
  placeholder?: string;
  messageId?: string;
  groupId?: string;
  projectConnectionId?: string;
  hideFromAdmins?: boolean;
  type?: MessageTypeDto;
  size?: "compact" | "large";
  invalid?: boolean;
  appearance?: "field" | "bare";
  disabled?: boolean;
  pending?: boolean;
  isEdit?: boolean;
  autoFocus?: boolean;
  submitOnEnter?: boolean;
  onSubmit?: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
  ref?: React.Ref<HTMLTextAreaElement>;
}

/**
 * Edits plain content and its structured mentions using the field or bare textarea appearance.
 * `value` carries plain content plus synchronized template and mention data, which `onChange` emits together.
 * Suggestions are available only while the composer is focused, and selection is rejected when it exceeds maxLength.
 * Disabled prevents editing, pending shows the textarea's progress state, and autoFocus forwards initial focus.
 * When submitOnEnter is enabled, Enter submits through onSubmit while Shift+Enter inserts a line break.
 * Focus changes are forwarded through onFocus and onBlur.
 */
export function MentionComposer({
  value,
  onChange,
  "data-testid": dataTestId,
  placeholder,
  messageId,
  groupId,
  projectConnectionId,
  hideFromAdmins,
  type,
  size = "compact",
  invalid,
  appearance = "field",
  disabled,
  pending,
  isEdit,
  autoFocus,
  submitOnEnter,
  onSubmit,
  onFocus,
  onBlur,
  ref,
  ...textareaProps
}: MentionComposerProps): React.ReactNode {
  const { t } = useTranslation();
  const queries = useCommunityFeedQueries();
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const listboxId = useId();
  const selectedMentions = restoreSelectedMentions(value);
  const [isFocused, setIsFocused] = useState(false);
  const [activeMention, setActiveMention] = useState<{ start: number; query: string }>();
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const active =
    activeMention &&
    value.content.slice(activeMention.start, activeMention.start + activeMention.query.length + 1) ===
      `@${activeMention.query}`
      ? activeMention
      : undefined;
  const visibleActive = isFocused ? active : undefined;
  const activeQuery = visibleActive?.query ?? "";
  const deferredQuery = useDeferredValue(activeQuery);

  const suggestionParams = {
    search: deferredQuery,
    messageId,
    groupId,
    projectConnectionId,
    hideFromAdmins,
    type,
  };
  const suggestionsQuery = useQuery({
    ...queries.mentionSuggestions(suggestionParams),
    enabled: visibleActive != null && deferredQuery.length > 0,
  });
  const suggestions = activeQuery === deferredQuery ? (suggestionsQuery.data ?? []) : [];
  const isSearching = activeQuery !== deferredQuery || suggestionsQuery.isPending;
  const selectedSuggestionIndex = suggestions.length === 0 ? 0 : Math.min(highlightedIndex, suggestions.length - 1);

  useEffect(() => {
    if (isEdit && inputRef.current) {
      const caret = inputRef.current.value.length;
      inputRef.current.focus();
      inputRef.current.setSelectionRange(caret, caret);
      inputRef.current.scrollTop = inputRef.current.scrollHeight;
    }
  }, [isEdit]);

  const emit = (content: string, mentions: SelectedMention[]) => {
    const structured = buildStructuredValue(content, mentions);
    onChange(structured);
  };

  const updateActive = (content: string, caret: number | null) => {
    if (caret == null) {
      setActiveMention(undefined);

      return;
    }
    const beforeCaret = content.slice(0, caret);
    const match = /(?:^|\s)@([^\s@]*)$/.exec(beforeCaret);
    const nextActive = match ? { start: beforeCaret.lastIndexOf("@"), query: match[1] } : undefined;
    if (nextActive?.query !== active?.query) {
      setHighlightedIndex(0);
    }
    setActiveMention(nextActive);
  };

  const handleTextChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const nextContent = event.target.value;
    const nextMentions = reconcileMentions(value.content, nextContent, selectedMentions);
    emit(nextContent, nextMentions);
    updateActive(nextContent, event.target.selectionStart);
  };

  const selectSuggestion = (suggestion: MentionSuggestionDto) => {
    if (!active) {
      return;
    }
    const caret = inputRef.current?.selectionStart ?? active.start + active.query.length + 1;
    const displayText = `@${suggestion.name}`;
    const needsSpace = value.content[caret] == null || !/\s/.test(value.content[caret]);
    const nextContent = `${value.content.slice(0, active.start)}${displayText}${needsSpace ? " " : ""}${value.content.slice(caret)}`;
    if (textareaProps.maxLength != null && nextContent.length > textareaProps.maxLength) {
      return;
    }
    const replacedLength = caret - active.start;
    const delta = displayText.length + (needsSpace ? 1 : 0) - replacedLength;
    const retained = selectedMentions
      .filter((mention) => mention.end <= active.start || mention.start >= caret)
      .map((mention) =>
        mention.start >= caret ? { ...mention, start: mention.start + delta, end: mention.end + delta } : mention,
      );
    const mention: SelectedMention = {
      id: crypto.randomUUID(),
      targetType: suggestion.targetType,
      targetId: suggestion.targetId,
      text: suggestion.name,
      start: active.start,
      end: active.start + displayText.length,
    };
    emit(
      nextContent,
      [...retained, mention].sort((a, b) => a.start - b.start),
    );
    setActiveMention(undefined);
    requestAnimationFrame(() => {
      const nextCaret = mention.end + (needsSpace ? 1 : 0);
      inputRef.current?.focus();
      inputRef.current?.setSelectionRange(nextCaret, nextCaret);
    });
  };

  const dropdown = (() => {
    if (!visibleActive) {
      return null;
    }
    if (visibleActive.query.length === 0) {
      return (
        <div
          data-testid="mention-search-prompt"
          className="flex items-center gap-space-xs px-select-option-inline py-select-option-block text-body text-text-secondary"
        >
          <span className="flex size-icon-inline items-center justify-center rounded-full bg-background-brand-light font-semibold text-text-brand-secondary">
            @
          </span>
          {t("component.mention-composer.prompt")}
        </div>
      );
    }
    if (isSearching) {
      return (
        <p role="status" className="px-select-option-inline py-select-option-block text-body text-text-secondary">
          {t("component.mention-composer.searching")}
        </p>
      );
    }
    if (suggestionsQuery.isError) {
      return (
        <p role="alert" className="px-select-option-inline py-select-option-block text-body text-text-secondary">
          {t("common.generic-error")}
        </p>
      );
    }
    if (suggestions.length === 0) {
      return (
        <p
          data-testid="mention-search-no-results"
          className="px-select-option-inline py-select-option-block text-body text-text-secondary"
        >
          {t("component.mention-composer.no-results")}
        </p>
      );
    }

    return suggestions.map((suggestion, index) => (
      <button
        key={`${suggestion.targetType}-${suggestion.targetId}`}
        id={`${listboxId}-option-${index}`}
        type="button"
        role="option"
        aria-selected={selectedSuggestionIndex === index}
        className={twJoin(
          "w-full cursor-pointer px-select-option-inline py-select-option-block text-left text-text-primary outline-hidden select-none",
          "focus-visible:ring-2 focus-visible:ring-border-focus focus-visible:ring-inset",
          selectedSuggestionIndex === index && "bg-background-brand-light",
        )}
        onMouseEnter={() => setHighlightedIndex(index)}
        onPointerDown={(event) => event.preventDefault()}
        onClick={() => selectSuggestion(suggestion)}
      >
        <span className="flex w-full items-center gap-space-sm">
          <UserAvatar
            image={suggestion.image}
            alt={suggestion.targetType === "company" ? "" : undefined}
            fallbackIcon={
              suggestion.targetType === "company" ? <Building02Icon tone="secondary" size={20} /> : undefined
            }
            size="sm"
          />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-body font-medium text-text-primary">{suggestion.name}</span>
            <span className="block truncate text-caption text-text-secondary">
              {suggestion.targetType === "company" && suggestion.eligibleMemberCount != null
                ? t("component.mention-composer.company-members", { count: suggestion.eligibleMemberCount })
                : suggestion.subtitle}
            </span>
          </span>
        </span>
      </button>
    ));
  })();

  return (
    <div className="relative w-full">
      <TextArea
        {...textareaProps}
        ref={mergeRefs(inputRef, ref)}
        data-testid={dataTestId}
        value={value.content}
        highlightedRanges={selectedMentions}
        hideScrollbar
        placeholder={placeholder}
        height={size === "large" ? "lg" : "compact"}
        appearance={appearance}
        invalid={invalid}
        disabled={disabled}
        pending={pending}
        autoFocus={autoFocus}
        role="combobox"
        aria-autocomplete="list"
        aria-busy={visibleActive != null && isSearching}
        aria-expanded={visibleActive != null}
        aria-controls={visibleActive ? listboxId : undefined}
        aria-activedescendant={
          visibleActive && suggestions.length > 0 ? `${listboxId}-option-${selectedSuggestionIndex}` : undefined
        }
        onFocus={(event) => {
          setIsFocused(true);
          updateActive(value.content, event.currentTarget.selectionStart);
          onFocus?.();
        }}
        onBlur={() => {
          setIsFocused(false);
          onBlur?.();
        }}
        onChange={handleTextChange}
        onClick={(event) => updateActive(value.content, event.currentTarget.selectionStart)}
        onKeyUp={(event) => {
          if (event.key !== "Escape") {
            updateActive(value.content, event.currentTarget.selectionStart);
          }
        }}
        onKeyDown={(event) => {
          if (event.nativeEvent.isComposing) {
            return;
          }
          if (active && event.key === "Escape") {
            setActiveMention(undefined);

            return;
          }
          if (active && suggestions.length > 0 && event.key === "ArrowDown") {
            event.preventDefault();
            setHighlightedIndex((index) => (index + 1) % suggestions.length);
          } else if (active && suggestions.length > 0 && event.key === "ArrowUp") {
            event.preventDefault();
            setHighlightedIndex((index) => (index - 1 + suggestions.length) % suggestions.length);
          } else if (active && suggestions.length > 0 && event.key === "Enter") {
            event.preventDefault();
            const suggestion = suggestions[selectedSuggestionIndex];
            if (suggestion) {
              selectSuggestion(suggestion);
            }
          } else if (active) {
            return;
          } else if (event.key === "Enter" && !event.shiftKey && submitOnEnter) {
            event.preventDefault();
            onSubmit?.();
          }
        }}
      />
      {dropdown && (
        <div
          id={listboxId}
          role="listbox"
          className="absolute inset-x-0 z-select-menu -mt-px flex max-h-searchable-select-menu min-h-control-sm flex-col items-start overflow-auto rounded-lg rounded-t-none border border-border-tertiary bg-background-default py-select-menu-edge shadow-searchable-select-menu outline-hidden!"
        >
          {dropdown}
        </div>
      )}
    </div>
  );
}

function restoreSelectedMentions(value: MentionComposerValue): SelectedMention[] {
  if (!value.contentTemplate || !value.mentions?.length) {
    return [];
  }
  const byId = new Map(value.mentions.map((mention) => [mention.id, mention]));
  const mentions: SelectedMention[] = [];
  let cursor = 0;
  let renderedCursor = 0;
  for (const match of value.contentTemplate.matchAll(/\{\{mention:([^}]+)}}/g)) {
    const mention = byId.get(match[1]);
    if (!mention || match.index == null) {
      continue;
    }
    renderedCursor += match.index - cursor;
    const display = `@${mention.text.replace(/^@/, "")}`;
    mentions.push({ ...mention, start: renderedCursor, end: renderedCursor + display.length });
    renderedCursor += display.length;
    cursor = match.index + match[0].length;
  }

  return mentions;
}

/** Restores editable mentions, keeping unavailable targets readable and falling back to plain content for invalid metadata. */
export function createMentionComposerValue({
  content,
  contentTemplate,
  mentions,
}: {
  content?: string;
  contentTemplate?: string;
  mentions?: MentionDto[];
}): MentionComposerValue {
  const readableContent = content ?? "";
  if (!contentTemplate || !mentions?.length) {
    return { content: readableContent };
  }

  let normalizedTemplate = contentTemplate;
  const availableMentions: MentionInputDto[] = [];
  for (const mention of mentions) {
    if (!mention.targetId) {
      const displayText = `@${mention.text.replace(/^@/, "")}`;
      normalizedTemplate = normalizedTemplate.replaceAll(`{{mention:${mention.id}}}`, displayText);
    } else {
      availableMentions.push({
        id: mention.id,
        targetType: mention.targetType,
        targetId: mention.targetId,
        text: mention.text.replace(/^@/, ""),
      });
    }
  }

  const expectedIds = new Set(availableMentions.map((mention) => mention.id));
  const tokenIds = [...normalizedTemplate.matchAll(/\{\{mention:([^}]+)}}/g)].map((match) => match[1]);
  const uniqueTokenIds = new Set(tokenIds);
  const hasExactMentionSet =
    expectedIds.size === availableMentions.length &&
    uniqueTokenIds.size === tokenIds.length &&
    uniqueTokenIds.size === expectedIds.size &&
    [...expectedIds].every((id) => uniqueTokenIds.has(id));
  if (!hasExactMentionSet) {
    return { content: readableContent };
  }

  return {
    content: readableContent,
    contentTemplate: normalizedTemplate,
    mentions: availableMentions,
  };
}

function reconcileMentions(oldContent: string, nextContent: string, mentions: SelectedMention[]): SelectedMention[] {
  let prefix = 0;
  while (prefix < oldContent.length && prefix < nextContent.length && oldContent[prefix] === nextContent[prefix]) {
    prefix++;
  }
  let oldSuffix = oldContent.length;
  let nextSuffix = nextContent.length;
  while (oldSuffix > prefix && nextSuffix > prefix && oldContent[oldSuffix - 1] === nextContent[nextSuffix - 1]) {
    oldSuffix--;
    nextSuffix--;
  }
  const delta = nextSuffix - prefix - (oldSuffix - prefix);

  return mentions
    .filter((mention) => mention.end <= prefix || mention.start >= oldSuffix)
    .map((mention) =>
      mention.start >= oldSuffix ? { ...mention, start: mention.start + delta, end: mention.end + delta } : mention,
    );
}

function buildStructuredValue(content: string, mentions: SelectedMention[]): MentionComposerValue {
  if (mentions.length === 0) {
    return { content };
  }
  let template = content;
  for (const mention of [...mentions].sort((a, b) => b.start - a.start)) {
    template = `${template.slice(0, mention.start)}{{mention:${mention.id}}}${template.slice(mention.end)}`;
  }

  return {
    content,
    contentTemplate: template,
    mentions: mentions.map(({ start: _start, end: _end, ...mention }) => mention),
  };
}
